import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { t } from '../i18n';

const API = window.location.origin + '/api';

const Layout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dealer = JSON.parse(localStorage.getItem('dealer') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = [1].includes(Number(dealer.id));

  const [pendingCounters, setPendingCounters] = useState(0);
  const [pendingMatches, setPendingMatches] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    axios.get(`${API}/messages/unread/count`, { headers }).then(res => {
      setUnreadMessages(res.data.count || 0);
    }).catch(() => {});

    axios.get(`${API}/matches`, { headers }).then(res => {
      const allMatches = res.data.matches || [];
      const pending = allMatches.filter((m: any) => {
        const iAmA = m.dealer_a_id === dealer.id;
        const myFeePaid = iAmA ? m.fee_a_paid : m.fee_b_paid;
        return m.status === 'pending' && !myFeePaid;
      });
      setPendingMatches(pending.length);
    }).catch(() => {});

    axios.get(`${API}/counters/received`, { headers }).then(res => {
      setPendingCounters(res.data.counters?.length || 0);
    }).catch(() => {});

    const interval = setInterval(() => {
      axios.get(`${API}/messages/unread/count`, { headers }).then(res => {
        setUnreadMessages(res.data.count || 0);
      }).catch(() => {});
    }, 30000);

    const fetchAll = () => {
      axios.get(`${API}/messages/unread/count`, { headers }).then(res => {
        setUnreadMessages(res.data.count || 0);
      }).catch(() => {});
    };

    window.addEventListener('refreshBadge', fetchAll);
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshBadge', fetchAll);
    };
  }, []);

  const navItems: {path:string,label:string,icon:string,badge?:number}[] = [
    { path: '/home', label: t('dashboard', 'Dashboard'), icon: '⊞' },
    { path: '/orderbook', label: t('orderBook', 'Order Book'), icon: '📋' },
    { path: '/new-offer', label: t('newOffer', 'New'), icon: '+' },
    { path: '/my-offers', label: t('myOffers', 'My'), icon: '📂', badge: pendingCounters },
    { path: '/matches', label: t('matches', 'Matches'), icon: '⇄', badge: pendingMatches + unreadMessages },
  ];

  const logout = () => {
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar desktop */}
      <div className="hidden md:flex flex-col w-56 bg-white border-r border-gray-200 min-h-screen p-4">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded flex items-center justify-center text-white font-black text-lg"
            style={{ backgroundColor: '#B8860B' }}>X</div>
          <span className="font-bold text-gray-900">BULLION X</span>
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map(item => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors relative ${
                location.pathname === item.path
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
              {(item.badge ?? 0) > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
          <Link to="/wishlist"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              location.pathname === '/wishlist' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <span>💡</span><span>Feedback</span>
          </Link>
          <Link to="/profile"
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              location.pathname === '/profile' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}>
            <span>👤</span><span>{t('profile', 'Profile')}</span>
          </Link>
          {isAdmin && (
            <Link to="/admin"
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                location.pathname === '/admin' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              <span>⚙️</span><span>{t('admin', 'Admin')}</span>
            </Link>
          )}
        </nav>

        <div className="border-t border-gray-200 pt-4 mt-4">
          <p className="text-xs text-gray-400 mb-1">0,1% {t('feePerMatchLabel', 'Fee per Match')}</p>
          <p className="text-sm font-semibold text-gray-900">{dealer.company_name}</p>
          <p className="text-xs text-gray-400">{dealer.country}</p>
          <button onClick={logout} className="text-xs text-gray-400 hover:text-gray-600 mt-2">
            {t('logout', 'Abmelden')}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        <div className="hidden md:flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
          <div />
          <p className="text-sm text-gray-500">{dealer.company_name} &nbsp;|&nbsp;
            <button onClick={logout} className="hover:text-gray-700" style={{ color: '#B8860B' }}>Out</button>
          </p>
        </div>
        <main className="flex-1 p-4 md:p-6 pb-20 md:pb-6">
          {children}
        </main>
      </div>

      {/* Bottom nav mobile */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex">
        {navItems.map(item => (
          <Link key={item.path} to={item.path}
            className={`flex-1 flex flex-col items-center py-2 text-xs relative ${
              location.pathname === item.path ? 'text-gray-900' : 'text-gray-400'
            }`}>
            <span className="text-lg">{item.icon}</span>
            <span>{item.label}</span>
            {(item.badge ?? 0) > 0 && (
              <span className="absolute top-1 right-1/4 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {item.badge}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Layout;
